import { useEffect, useState } from "react";
import { Tag, Loader2, RefreshCw } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import apiClient from "@/lib/api";
import {
  formatRupiah, discountLabel, formatPromoDate,
  PROMO_STATUS_LABELS, PROMO_STATUS_STYLES,
} from "@/components/promo/promoMeta";

const FILTERS = [
  { value: "all", label: "Semua" },
  { value: "active", label: "Aktif" },
  { value: "inactive", label: "Nonaktif" },
];

export default function AdminPromosPage() {
  const [promos, setPromos] = useState([]);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = (s = status) => {
    setLoading(true);
    setError(false);
    apiClient.get("/admin/promos", { params: s !== "all" ? { status: s } : {} })
      .then((r) => setPromos(r.data || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(status); /* eslint-disable-next-line */ }, [status]);

  return (
    <AdminLayout testid="admin-promos-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Promo &amp; Diskon</h1>
          <p className="mt-2 text-sm text-muted-foreground">Monitoring seluruh promo yang dibuat oleh Owner (hanya dapat dilihat).</p>
        </div>
        <button onClick={() => load()} data-testid="admin-promos-refresh"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-primary border border-border hover:bg-secondary/60 transition-colors">
          <RefreshCw size={14} /> Muat Ulang
        </button>
      </div>

      <div className="mt-5 flex gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button key={f.value} onClick={() => setStatus(f.value)} data-testid={`admin-promos-filter-${f.value}`}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              status === f.value ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary/60"
            }`}>{f.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="mt-10 grid place-items-center py-16"><Loader2 className="animate-spin text-primary" size={28} /></div>
      ) : error ? (
        <div className="mt-8 rounded-2xl border border-destructive/30 bg-destructive/10 p-8 text-center" data-testid="admin-promos-error">
          <p className="text-sm text-destructive">Gagal memuat promo.</p>
          <button onClick={() => load()} className="mt-3 text-sm font-semibold text-primary hover:underline">Coba lagi</button>
        </div>
      ) : promos.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-border bg-secondary/30 p-12 text-center" data-testid="admin-promos-empty">
          <span className="grid place-items-center w-14 h-14 rounded-2xl bg-secondary text-primary mx-auto mb-4"><Tag size={26} /></span>
          <h3 className="font-heading text-lg font-medium text-foreground">Belum ada promo.</h3>
        </div>
      ) : (
        <div className="mt-6 overflow-x-auto rounded-2xl border border-border" data-testid="admin-promos-list">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-muted-foreground">
              <tr className="text-left">
                <th className="px-4 py-3 font-semibold">Promo</th>
                <th className="px-4 py-3 font-semibold">Property</th>
                <th className="px-4 py-3 font-semibold">Diskon</th>
                <th className="px-4 py-3 font-semibold">Periode</th>
                <th className="px-4 py-3 font-semibold">Dipakai</th>
                <th className="px-4 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {promos.map((p, i) => (
                <tr key={p.id} data-testid={`admin-promo-${i}`} className="border-t border-border">
                  <td className="px-4 py-3">
                    <div className="font-medium">{p.promo_name}</div>
                    <div className="font-mono text-xs text-primary">{p.promo_code}</div>
                  </td>
                  <td className="px-4 py-3">{p.property_name || "-"}</td>
                  <td className="px-4 py-3 font-semibold text-primary">{discountLabel(p)}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{formatPromoDate(p.start_date)} – {formatPromoDate(p.end_date)}</td>
                  <td className="px-4 py-3">{p.usage_limit != null ? `${p.usage_count}/${p.usage_limit}` : p.usage_count}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${PROMO_STATUS_STYLES[p.status] || ""}`}>{PROMO_STATUS_LABELS[p.status] || p.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
